"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TimeEntrySyncService = exports.SYNC_EVENTS = void 0;
const model_1 = require("./model");
const api_1 = require("./api");
const error_1 = require("./error");
const utils_1 = require("./utils");
const encoding_1 = require("./encoding");
exports.SYNC_EVENTS = {
    TIME_ENTRY_STATE_CHANGED: "TimeEntryStateChanged",
};
class TimeEntrySyncService {
    constructor(api, crypto, state, throttleDelayMs = 1000) {
        this.api = api;
        this.crypto = crypto;
        this.state = state;
        this.throttleDelayMs = throttleDelayMs;
        this._activeSync = null;
        this._queuedSync = null;
        this._eventHandlers = new Map();
        this._eventHandlers.set(exports.SYNC_EVENTS.TIME_ENTRY_STATE_CHANGED, []);
        // Create throttled function with configurable delay
        this.throttledSyncTimeEntries = (0, utils_1.throttle)(async () => {
            void this.syncTimeEntries();
        }, this.throttleDelayMs);
    }
    emit(event) {
        const handlers = this._eventHandlers.get(event) || [];
        handlers.forEach((handler) => handler());
    }
    on(event, handler) {
        const handlers = this._eventHandlers.get(event) || [];
        handlers.push(handler);
        this._eventHandlers.set(event, handlers);
    }
    async createOrUpdateTimeEntries(entries, options = {}) {
        const { wait = false, publish = false, setUpdated = true } = options;
        entries = Array.isArray(entries) ? entries : [entries];
        for (const e of entries) {
            if (setUpdated) {
                e.updated = new Date();
            }
            if (!e.id) {
                e.id = await this.crypto.uuid();
            }
            if (publish ||
                ![model_1.TimeEntryType.Work, model_1.TimeEntryType.CompDay, model_1.TimeEntryType.Free].includes(e.type) ||
                e.isPast) {
                e.setPublished();
            }
            this.state.updatedTimeEntries.set(e.id, e);
        }
        if (wait) {
            await this.syncTimeEntries();
        }
        else {
            this.throttledSyncTimeEntries();
        }
        return entries;
    }
    async removeTimeEntries(entries, wait = false, publish = false) {
        entries = Array.isArray(entries) ? entries : [entries];
        entries.forEach((e) => {
            const date = new Date();
            e.deleted = date;
            e.updated = date;
            if (!e.published) {
                this.state.updatedTimeEntries.delete(e.id);
                this.state.removedTimeEntries.set(e.id, e);
            }
            else {
                if (publish ||
                    ![model_1.TimeEntryType.Work, model_1.TimeEntryType.CompDay, model_1.TimeEntryType.Free].includes(e.type) ||
                    e.isPast) {
                    e.setPublished();
                }
                this.state.updatedTimeEntries.set(e.id, e);
            }
        });
        if (wait) {
            await this.syncTimeEntries();
        }
        else {
            this.throttledSyncTimeEntries();
        }
    }
    async syncTimeEntries() {
        let queued = this._queuedSync;
        let active = this._activeSync;
        if (queued) {
            // There is already a queued sync promise, so just return that one
            return queued;
        }
        if (active) {
            // There is already a synchronization in process. wait for the current sync to finish
            // before starting a new one.
            const next = () => {
                this._queuedSync = null;
                return this.syncTimeEntries();
            };
            queued = active.then(next, next);
            this._queuedSync = queued;
            return queued;
        }
        active = this._syncTimeEntries().then(() => {
            this._activeSync = null;
        }, (error) => {
            this._activeSync = null;
            error.display = true;
            throw error;
        });
        this._activeSync = active;
        return active;
    }
    isUpdated(entry) {
        return this.state.updatedTimeEntries.has(entry.id);
    }
    isRemoved(entry) {
        return this.state.removedTimeEntries.has(entry.id);
    }
    clearSyncState() {
        this.state.updatedTimeEntries.clear();
        this.state.removedTimeEntries.clear();
        this.emit(exports.SYNC_EVENTS.TIME_ENTRY_STATE_CHANGED);
    }
    async _syncTimeEntries() {
        const updated = [...this.state.updatedTimeEntries.values()].map(encoding_1.clone);
        const removed = [...this.state.removedTimeEntries.values()];
        if (updated.length || removed.length) {
            try {
                await this.api.updateTimeEntries(new api_1.UpdateTimeEntriesParams({ updated, removed }));
                removed.forEach((e) => this.state.removedTimeEntries.delete(e.id));
                updated.forEach((entry) => {
                    const updatedEntry = this.state.updatedTimeEntries.get(entry.id);
                    if (updatedEntry && updatedEntry.updated <= entry.updated) {
                        this.state.updatedTimeEntries.delete(entry.id);
                    }
                });
                // Emit event to signal that state should be saved
                this.emit(exports.SYNC_EVENTS.TIME_ENTRY_STATE_CHANGED);
            }
            catch (e) {
                if (e.code !== error_1.ErrorCode.FAILED_CONNECTION) {
                    this.state.updatedTimeEntries.clear();
                    this.state.removedTimeEntries.clear();
                    // Emit event to signal that state should be saved
                    this.emit(exports.SYNC_EVENTS.TIME_ENTRY_STATE_CHANGED);
                    throw e;
                }
            }
        }
    }
}
exports.TimeEntrySyncService = TimeEntrySyncService;
//# sourceMappingURL=time-entry-sync-service.js.map